/// <reference types="@angular/localize" />

import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import type { Breadcrumb, ErrorEvent, EventHint, TransactionEvent } from '@sentry/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from './environments/environment';
import { SENTRY_RELEASE } from './environments/sentry-release';
import { MsalConfigService } from './app/auth/msal-config.service';
import { AppModule } from './app/app.module';
import {
  SENTRY_BREADCRUMB_CATEGORIES,
  SENTRY_BREADCRUMB_DATA_KEYS,
  SENTRY_CONTEXT_KEYS,
  SENTRY_TAG_KEYS,
  SENTRY_TRACE_DEFAULT_TARGETS,
} from './app/core/sentry/sentry-constants';
import { sanitizeHeaders, sanitizeRequestData, sanitizeUrl, sanitizeValue } from './app/core/sentry/sentry-utils';

const release = SENTRY_RELEASE || environment.sentry['release'] || undefined;
const sentryEnvironment =
  environment.sentry.environment || (environment.production ? 'production' : 'development');
const traceTargets = buildTracePropagationTargets([
  environment.identity_url,
  environment.identity_europe_url,
  environment.tracking_url,
  environment.products_url,
  environment.marketplace_url,
  environment.cafler_backoffice_back_url,
  environment.cafler_vehicles_url,
  environment.copec_data_url,
  environment.WIDGET_BASE_URL,
  environment.BASE_URL,
]);

type HttpLikeError = {
  status?: number;
  statusText?: string;
  url?: string;
  message?: string;
  name?: string;
  error?: unknown;
};

var consoleBridgeInstalled = false;
const recentConsoleErrors = new Map<string, number>();
const CONSOLE_DEDUP_WINDOW_MS = 5000;

if (environment.sentry.enabled && environment.sentry.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: sentryEnvironment,
    sendDefaultPii: environment.sentry.sendDefaultPii,
    enableLogs: environment.sentry.enableLogs,
    release,
    tracePropagationTargets: traceTargets,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: environment.sentry.tracesSampleRate,
    replaysSessionSampleRate: environment.sentry.replaySampleRate,
    replaysOnErrorSampleRate: environment.sentry.replayOnErrorSampleRate,
    beforeSend: (event: ErrorEvent, hint: EventHint) => sanitizeErrorEvent(event, hint),
    beforeSendTransaction: (event: TransactionEvent, _hint: EventHint) =>
      sanitizeTransactionEvent(event),
    beforeBreadcrumb: (breadcrumb: Breadcrumb) => sanitizeBreadcrumb(breadcrumb),
  });

  Sentry.setTag(SENTRY_TAG_KEYS.environment, sentryEnvironment);

  const appContext: Record<string, string> = { environment: sentryEnvironment };
  if (release) {
    Sentry.setTag(SENTRY_TAG_KEYS.appRelease, release);
    appContext['release'] = release;
  }
  Sentry.setContext(SENTRY_CONTEXT_KEYS.app, appContext);

  installConsoleErrorBridge();
}

if (document.readyState !== 'loading') {
  initializeApp();
} else {
  document.addEventListener('DOMContentLoaded', initializeApp);
}

async function initializeApp() {
  try {
    await MsalConfigService.createMsalInstance().initialize();
    platformBrowserDynamic()
      .bootstrapModule(AppModule)
      .catch((err) => console.error(err));
  } catch (error) {
    console.error('MSAL initialization failed:', error);
  }
}

type SentryEventLike = {
  request?: {
    url?: string;
    headers?: Record<string, string>;
    cookies?: unknown;
    data?: unknown;
  };
  contexts?: Record<string, unknown>;
  extra?: Record<string, unknown>;
};

function sanitizeErrorEvent(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  if (isMsalNonBlockingError(hint)) return null;
  normalizeObjectCapturedEvent(event, hint);
  tagBrowserAuthEvent(event);
  return sanitizeEventCommon(event);
}

function sanitizeTransactionEvent(event: TransactionEvent): TransactionEvent {
  return sanitizeEventCommon(event);
}

function sanitizeEventCommon<T extends SentryEventLike>(event: T): T {
  if (event.request?.url) {
    event.request.url = sanitizeUrl(event.request.url);
  }

  if (event.request?.headers) {
    const headers = sanitizeHeaders(event.request.headers);
    event.request.headers = headers;
  }

  if (event.request?.cookies) {
    delete event.request.cookies;
  }

  if (event.request?.data) {
    event.request.data = sanitizeRequestData(event.request.data);
  }

  if (event.contexts) {
    event.contexts = sanitizeValue(event.contexts) as Record<string, unknown>;
  }

  if (event.extra) {
    event.extra = sanitizeValue(event.extra) as Record<string, unknown>;
  }

  return event;
}

function sanitizeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (!breadcrumb) return breadcrumb;

  const sanitized: Breadcrumb = { ...breadcrumb };

  if (sanitized.category === SENTRY_BREADCRUMB_CATEGORIES.navigation && sanitized.data?.[SENTRY_BREADCRUMB_DATA_KEYS.to]) {
    sanitized.data = {
      ...sanitized.data,
      [SENTRY_BREADCRUMB_DATA_KEYS.to]: sanitizeUrl(
        String(sanitized.data[SENTRY_BREADCRUMB_DATA_KEYS.to])
      ),
    };
  }

  if (
    sanitized.category === SENTRY_BREADCRUMB_CATEGORIES.xhr ||
    sanitized.category === SENTRY_BREADCRUMB_CATEGORIES.fetch
  ) {
    if (sanitized.data?.[SENTRY_BREADCRUMB_DATA_KEYS.url]) {
      sanitized.data = {
        ...sanitized.data,
        [SENTRY_BREADCRUMB_DATA_KEYS.url]: sanitizeUrl(
          String(sanitized.data[SENTRY_BREADCRUMB_DATA_KEYS.url])
        ),
      };
    }
    if (sanitized.data?.[SENTRY_BREADCRUMB_DATA_KEYS.requestBody]) {
      sanitized.data = {
        ...sanitized.data,
        [SENTRY_BREADCRUMB_DATA_KEYS.requestBody]: sanitizeRequestData(
          sanitized.data[SENTRY_BREADCRUMB_DATA_KEYS.requestBody]
        ),
      };
    }
  }

  if (sanitized.data) {
    sanitized.data = sanitizeValue(sanitized.data) as Breadcrumb['data'];
  }

  return sanitized;
}

function buildTracePropagationTargets(urls: string[]): string[] {
  const targets: string[] = [...SENTRY_TRACE_DEFAULT_TARGETS];

  urls.forEach((value) => {
    if (!value) return;
    try {
      const origin = new URL(value).origin;
      if (!targets.includes(origin)) {
        targets.push(origin);
      }
    } catch {
      // ignore invalid URLs
    }
  });

  return targets;
}

function installConsoleErrorBridge(): void {
  if (consoleBridgeInstalled) return;
  consoleBridgeInstalled = true;

  const originalConsoleError = console.error.bind(console);

  console.error = (...args: unknown[]) => {
    originalConsoleError(...args);

    const parsed = getConsoleErrorPayload(args);
    if (!parsed) return;
    if (isRecentlyCaptured(parsed.dedupeKey)) return;

    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setTag('error_source', 'console');
      scope.setTag('user_blocking', String(parsed.userBlocking));

      if (parsed.httpStatus != null) {
        scope.setTag(SENTRY_TAG_KEYS.httpStatus, String(parsed.httpStatus));
        scope.setTag(
          SENTRY_TAG_KEYS.httpStatusFamily,
          parsed.httpStatus === 0 ? '0' : `${Math.floor(parsed.httpStatus / 100)}xx`
        );
      }

      Sentry.captureException(parsed.error);
    });
  };
}

function getConsoleErrorPayload(
  args: unknown[]
): { error: Error; dedupeKey: string; userBlocking: boolean; httpStatus?: number } | null {
  const directError = args.find((arg): arg is Error => arg instanceof Error);
  if (directError) {
    const directText = `${directError.name ?? ''} ${directError.message ?? ''}`.toLowerCase();
    if (MSAL_NON_BLOCKING_ERRORS.some(code => directText.includes(code))) {
      return null;
    }
    const dedupeKey = `${directError.name}:${directError.message}`;
    return {
      error: directError,
      dedupeKey,
      userBlocking: isUserBlockingRuntimeError(directError),
    };
  }

  const httpLike = args.find((arg): arg is HttpLikeError => isHttpLikeError(arg));
  if (httpLike) {
    const status = Number(httpLike.status) || 0;
    const statusText = httpLike.statusText || (status === 0 ? 'Network Error' : 'HTTP Error');
    const sanitizedUrl = sanitizeUrl(httpLike.url || '');
    const message = `[Console HTTP ${status}] ${sanitizedUrl || 'unknown_url'} (${statusText})`;
    const error = new Error(message);
    error.name = 'ConsoleHttpError';
    return {
      error,
      dedupeKey: message,
      userBlocking: status === 0 || status >= 400,
      httpStatus: status,
    };
  }

  return null;
}

function normalizeObjectCapturedEvent(event: ErrorEvent, hint: EventHint): void {
  const original = hint.originalException;
  if (!isHttpLikeError(original)) return;

  const status = Number(original.status) || 0;
  const statusText = original.statusText || (status === 0 ? 'Network Error' : 'HTTP Error');
  const sanitizedUrl = sanitizeUrl(String(original.url || ''));
  const message = `[HTTP ${status}] ${sanitizedUrl || 'unknown_url'} (${statusText})`;

  event.message = message;
  event.level = 'error';
  event.fingerprint = ['http', String(status), sanitizedUrl || 'unknown_url'];
  event.tags = {
    ...event.tags,
    [SENTRY_TAG_KEYS.httpStatus]: String(status),
    [SENTRY_TAG_KEYS.httpStatusFamily]: status === 0 ? '0' : `${Math.floor(status / 100)}xx`,
    [SENTRY_TAG_KEYS.httpErrorKind]: status === 0 ? 'network' : status >= 500 ? 'server' : 'client',
    [SENTRY_TAG_KEYS.userBlocking]: String(status === 0 || status >= 400),
  };
  event.contexts = {
    ...event.contexts,
    [SENTRY_CONTEXT_KEYS.http]: {
      status,
      statusText,
      url: sanitizedUrl,
      response: {
        body: sanitizeRequestData(original.error),
      },
    },
  };
}

function tagBrowserAuthEvent(event: ErrorEvent): void {
  const exception = event.exception?.values?.[0];
  const rawText = `${exception?.type || ''} ${exception?.value || ''} ${event.message || ''}`.toLowerCase();
  if (!rawText.includes('browserautherror')) return;

  const authErrorCode = rawText.includes('interaction_in_progress')
    ? 'interaction_in_progress'
    : rawText.includes('redirect_in_iframe')
      ? 'redirect_in_iframe'
      : 'unknown';

  event.tags = {
    ...event.tags,
    user_blocking: 'true',
    auth_error: 'msal',
    auth_error_code: authErrorCode,
  };
}

const MSAL_NON_BLOCKING_ERRORS = [
  'hash_empty_error',
  'hash_does_not_contain',
  'no_cached_authority',
  'monitor_window_timeout',
  'block_iframe_reload',
  'no_tokens_found',
  'no token available',
  'popup_window_error',
  'post_request_failed',
  'interaction_required',
  'interaction_in_progress',
  'endpoints_resolution_error',
];

function isMsalNonBlockingError(hint: EventHint): boolean {
  const error = hint.originalException;
  if (!error || typeof error !== 'object') return false;
  const text = `${(error as Error).name ?? ''} ${(error as Error).message ?? ''}`.toLowerCase();
  return MSAL_NON_BLOCKING_ERRORS.some(code => text.includes(code));
}

function isHttpLikeError(value: unknown): value is HttpLikeError {
  if (value instanceof HttpErrorResponse) return true;
  if (!value || typeof value !== 'object') return false;
  const candidate = value as HttpLikeError;
  return typeof candidate.status === 'number' && typeof candidate.statusText === 'string';
}

function isUserBlockingRuntimeError(error: Error): boolean {
  const text = `${error.name} ${error.message}`.toLowerCase();
  return /browserautherror|typeerror|cannot read|is not a function|interaction_in_progress|redirect_in_iframe/.test(
    text
  );
}

function isRecentlyCaptured(key: string): boolean {
  const now = Date.now();
  const last = recentConsoleErrors.get(key);
  recentConsoleErrors.set(key, now);

  for (const [entryKey, entryTs] of recentConsoleErrors.entries()) {
    if (now - entryTs > CONSOLE_DEDUP_WINDOW_MS) {
      recentConsoleErrors.delete(entryKey);
    }
  }

  return typeof last === 'number' && now - last < CONSOLE_DEDUP_WINDOW_MS;
}
